/**
 * WIZDRAFT-1 / WIZDRAFT-2 · 向导的服务器端草稿（后端 routes/wizard_drafts.py，通用接口 ``/api/v1/wizard-drafts?kind=``）。
 * 草稿**不是正式数据**：向导前端状态原样存进 payload，后端不解析（上限 1MB）。同租户有该向导「新建」权限的人都能看到、接手。
 *
 * 🔴 ``WIZARD_KINDS`` 和后端 ``services/wizard_drafts.KIND_PERMS`` 一一对应；新增向导两边各加一行。
 */
import { api } from "./api";

const BASE = "/api/v1/wizard-drafts";

export type WizardKind = "product" | "hotel" | "attraction" | "supplier" | "channel_partner" | "order";

/** 每个向导：要的新建权限（列表黄条按它显示）· 黄条里的名词 · 「继续」跳回的向导地址 · steps = 向导有几步（1 = 单页表单，黄条不写「做到第 N 步」） */
export const WIZARD_KINDS: Record<WizardKind, { perm: string; noun: string; newPath: string; steps?: number }> = {
  product: { perm: "products.products.create", noun: "新建产品", newPath: "/products/new", steps: 5 },
  hotel: { perm: "hotels.hotels.create", noun: "新建酒店", newPath: "/hotels/new", steps: 1 },
  attraction: { perm: "attractions.attractions.create", noun: "新建景点门票", newPath: "/attractions/new", steps: 4 },
  supplier: { perm: "suppliers.suppliers.create", noun: "新建供应商", newPath: "/suppliers/new", steps: 2 },
  channel_partner: { perm: "channel_partners.partners.create", noun: "新建同行客户", newPath: "/admin/channel-partners/new" },
  order: { perm: "orders.orders.create", noun: "新建订单", newPath: "/orders/new" },
};

export interface WizardDraftBrief {
  id: string;
  kind: WizardKind;
  title: string | null;     // 没起名 = null
  step: number;             // 1–10（各向导自己的步数）
  created_by_name: string | null;
  updated_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}
export interface WizardDraft extends WizardDraftBrief {
  payload: Record<string, unknown>;
}
export interface WizardDraftInput {
  title: string | null;
  step: number;
  payload: Record<string, unknown>;
}

export const wizardDraftsApi = {
  list: (kind: WizardKind) => api<WizardDraftBrief[]>(`${BASE}?kind=${kind}`),
  get: (kind: WizardKind, id: string) => api<WizardDraft>(`${BASE}/${id}?kind=${kind}`),
  create: (kind: WizardKind, body: WizardDraftInput) => api<WizardDraft>(`${BASE}?kind=${kind}`, { method: "POST", body: JSON.stringify(body) }),
  update: (kind: WizardKind, id: string, body: WizardDraftInput) => api<WizardDraft>(`${BASE}/${id}?kind=${kind}`, { method: "PUT", body: JSON.stringify(body) }),
  remove: (kind: WizardKind, id: string) => api<void>(`${BASE}/${id}?kind=${kind}`, { method: "DELETE" }),
};

/** 「今天 08:52」「昨天 16:10」「10-20 09:30」（本地时间） */
export function draftTimeLabel(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p2 = (n: number) => String(n).padStart(2, "0");
  const hm = `${p2(d.getHours())}:${p2(d.getMinutes())}`;
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  if (diff === 0) return `今天 ${hm}`;
  if (diff === 1) return `昨天 ${hm}`;
  return `${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${hm}`;
}
